import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SITE from '../api';
import { useSite, IMG, SocialIcons } from '../components/StoreLayout';
import StorePhoneInput from '../components/StorePhoneInput';
import Captcha from '../components/Captcha';
import useSEO from '../useSEO';
import { trackEvent } from '../analytics';
import { STORE_BASE } from '../routeBase';

export default function ContactPage() {
  const { content: c } = useSite();
  const [offices, setOffices] = useState([]);
  const [f, setF] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const set = k => e => setF(prev => ({ ...prev, [k]: e.target.value }));

  useSEO({
    title: c['contact.meta_title'] || c['contact.title'] || 'Contact Us',
    description: c['contact.meta_description'] || `Contact ${c['brand.name'] || 'Salvador Eyewear'} for wholesale optical frame pricing, catalogues and export enquiries.`,
    keywords: c['contact.meta_keywords'] || 'contact Salvador Eyewear, wholesale eyewear enquiry, eyewear manufacturer contact',
    canonical: `${window.location.origin}/store/contact`,
  });

  useEffect(() => { SITE.get('/entities').then(r => setOffices(r.data.offices || [])); }, []);

  const submit = async e => {
    e.preventDefault(); setErr('');
    if (!f.message) return setErr('Please enter a message');
    if (!f.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return setErr('Please enter a valid email address');
    if (!f.phone || !f.phone.trim()) return setErr('Please enter your phone number');
    setBusy(true);
    try {
      await SITE.post('/enquiry', { ...f, source: 'contact', captcha_token: captchaToken });
      setSent(true);
      trackEvent('contact_form_submission', { subject: f.subject || '' });
    }
    catch (e2) { setErr(e2.response?.data?.error || 'Could not send'); }
    setBusy(false);
  };

  const hq = offices.find(o => o.is_parent) || offices[0] || null;
  const branches = offices.filter(o => o !== hq);

  return (
    <div>
      <style>{`
        .sf-2col { display:grid; gap:36px; grid-template-columns:1fr 1fr; }
        .sf-branch-grid { display:grid; gap:24px; grid-template-columns:1fr 1fr; }
        .sf-branch-card { display:flex; gap:18px; align-items:flex-start; }
        @media (max-width:760px) {
          .sf-2col, .sf-branch-grid { grid-template-columns:1fr; }
          .sf-branch-card { flex-direction:column; }
        }
      `}</style>

      {/* Title / banner — aspect-ratio matches the 1200x400 "best size" hint
          in the admin editor. Without it, a fluid-width box with only a
          minHeight floor rarely has the same shape as the uploaded image, so
          background-size:cover silently zooms in and crops whatever doesn't
          fit — worse the wider the screen, since width grows but height
          doesn't. minHeight stays as a floor for narrow/mobile screens. */}
      {c['contact.image'] ? (
        <div style={{ position: 'relative', minHeight: 220, aspectRatio: '1200 / 400', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: `url(${IMG(c['contact.image'])}) center/cover` }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
          <div style={{ position: 'relative', color: '#fff', padding: '24px' }}>
            <h1 style={{ fontSize: 36, fontWeight: 700, margin: 0 }}>{c['contact.title'] || 'Contact Us'}</h1>
            {c['contact.subheader'] && <p style={{ fontSize: 15, margin: '10px 0 0', opacity: 0.92 }}>{c['contact.subheader']}</p>}
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '54px 24px 0' }}>
          <h1 style={{ fontSize: 36, fontWeight: 700, margin: 0 }}>{c['contact.title'] || 'Contact Us'}</h1>
          {c['contact.subheader'] && <p style={{ fontSize: 15.5, color: '#666', margin: '12px auto 0', maxWidth: 560 }}>{c['contact.subheader']}</p>}
        </div>
      )}
      {c['contact.intro'] && (
        <p style={{ maxWidth: 640, margin: '14px auto 0', padding: '0 24px', color: '#888', fontSize: 13.5, lineHeight: 1.7, textAlign: 'center' }}>
          {c['contact.intro']}
        </p>
      )}

      {/* Head office: rectangle map, overlay card, then Contact Information | form */}
      {hq && (
        <section style={{ maxWidth: 1100, margin: '44px auto 0', padding: '0 24px' }}>
          <MapPanel office={hq} height={420} />
        </section>
      )}

      <section style={{ maxWidth: 1100, margin: hq ? '36px auto 60px' : '36px auto 60px', padding: '0 24px' }}>
        <div className="sf-2col">
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>{c['contact.hq_heading'] || 'Contact Information'}</h2>
            {hq && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 18 }}>
                {hq.address && <InfoRow icon="ti-map-pin" label="Address" value={hq.address} />}
                {hq.working_hours && <InfoRow icon="ti-clock" label="Working Hours" value={hq.working_hours} multiline />}
                {hq.phone && (
                  <InfoRow icon="ti-phone" label="Phone" value={
                    <a href={`tel:${hq.phone.replace(/\s+/g, '')}`} style={{ color: 'inherit', textDecoration: 'none' }}
                      onClick={() => trackEvent('phone_click', { location: 'contact_page_hq' })}>{hq.phone}</a>
                  } />
                )}
                {hq.email && <InfoRow icon="ti-mail" label="Email" value={hq.email} />}
              </div>
            )}
            <SocialIcons c={c} size={36} />
          </div>

          <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 16, padding: 30 }}>
            {sent ? (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <i className="ti ti-circle-check" style={{ fontSize: 44, color: '#16a34a' }} />
                <h3 style={{ marginTop: 10 }}>Thank you!</h3>
                <p style={{ color: '#666', fontSize: 14 }}>Your enquiry has been sent. We will contact you soon.</p>
              </div>
            ) : (
              <form onSubmit={submit}>
                <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{c['contact.form_heading'] || 'Got Any Questions?'}</h3>
                {c['contact.form_subtext'] && <p style={{ fontSize: 13.5, color: '#888', marginBottom: 18 }}>{c['contact.form_subtext']}</p>}
                {err && <div style={errBox}>{err}</div>}
                <div className="sf-form-row" style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}><I label="Name *" value={f.name} onChange={set('name')} required /></div>
                  <div style={{ flex: 1 }}><I label="Email *" type="email" value={f.email} onChange={set('email')} required /></div>
                </div>
                <div className="sf-form-row" style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Phone *</label>
                    <StorePhoneInput value={f.phone} onChange={v => setF(prev => ({ ...prev, phone: v }))} required />
                  </div>
                  <div style={{ flex: 1 }}><I label="Subject" value={f.subject} onChange={set('subject')} /></div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={lbl}>Message *</label>
                  <textarea value={f.message} onChange={set('message')} rows={5} style={{ ...inp, resize: 'vertical' }} />
                </div>
                <Captcha onToken={setCaptchaToken} />
                <button type="submit" disabled={busy} className="sf-btn-outline" style={submitBtn}>{busy ? 'Sending…' : 'Submit'}</button>
                <p style={{ fontSize: 11.5, color: '#999', textAlign: 'center', marginTop: 14 }}>
                  By submitting, you agree to our{' '}
                  <Link to={`${STORE_BASE}/page/terms-and-conditions`} style={legalLink}>Terms &amp; Conditions</Link>
                  {' '}and{' '}
                  <Link to={`${STORE_BASE}/page/privacy-and-cookies`} style={legalLink}>Privacy &amp; Data Policy</Link>.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Branch / offshore offices — "Our Offices" grid, auto-populated from
          every non-headquarters entity marked "show on Contact Us page".
          Adding a new entity in the admin panel adds it here automatically. */}
      {branches.length > 0 && (
        <section style={{ maxWidth: 1100, margin: '0 auto 70px', padding: '0 24px' }}>
          <h2 style={{ fontSize: 26, fontWeight: 700, textAlign: 'center', marginBottom: 36 }}>{c['contact.offices_heading'] || 'Our Offices'}</h2>
          <div className="sf-branch-grid">
            {branches.map(o => <BranchCard key={o.id} office={o} />)}
          </div>
        </section>
      )}
    </div>
  );
}

// ── Flag icon — uses the ISO country_code (already stored on every entity)
// against a free flag-image CDN, since flag emoji render as plain "IN"/"KE"
// text on Windows (no default color-emoji font ships with flag glyphs).
function FlagIcon({ countryCode, size = 20 }) {
  if (!countryCode) return null;
  return (
    <img
      src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
      alt="" width={size} style={{ borderRadius: 2, verticalAlign: 'middle', marginRight: 8, boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }}
      onError={e => { e.target.style.display = 'none'; }}
    />
  );
}

// Only a Google Maps "Embed a map" src (or one we resolved server-side from
// a shortened maps.app.goo.gl link) can go in an iframe without an API key.
function MapPanel({ office, height, showCard = true }) {
  const embedSrc = office.google_maps_embed_url;
  if (!embedSrc) {
    // Resolution failed or no link yet — a clean placeholder, not a broken box.
    return office.google_maps_link ? (
      <a href={office.google_maps_link} target="_blank" rel="noreferrer" style={{ ...viewMapLink, height }}>
        <i className="ti ti-map-pin" /> {showCard ? 'View on Google Maps ↗' : ''}
      </a>
    ) : (
      <div style={{ height, borderRadius: 16, background: '#f7f8fa', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 13 }}>
        {showCard ? 'Map coming soon' : <i className="ti ti-map-pin" />}
      </div>
    );
  }
  return (
    <div style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
      <iframe title={office.name} src={embedSrc} width="100%" height={height} style={{ border: 0, display: 'block' }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
      {/* The overlay info card only fits a tall hero-sized map (the head
          office panel) — small branch-card thumbnails just show the raw map
          plus an "Open in Maps" corner button, since their full address/
          hours already show in the card text beside the thumbnail. */}
      {showCard && office.google_maps_link && (
        <a href={office.google_maps_link} target="_blank" rel="noreferrer" style={openMapBtn}>Open in Maps ↗</a>
      )}
      {showCard && (office.address || office.working_hours) && (
        <div style={mapCard}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
            <FlagIcon countryCode={office.country_code} size={18} />
            <Link to={`${STORE_BASE}/entity/${office.code}`} style={{ color: 'inherit', textDecoration: 'none' }} onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>{office.name}</Link>
          </div>
          {office.address && <div style={{ fontSize: 12.5, color: '#555', marginBottom: 10, lineHeight: 1.5 }}>{office.address}</div>}
          {office.working_hours && <div style={{ fontSize: 11.5, color: '#555', whiteSpace: 'pre-line', marginBottom: 12, lineHeight: 1.6 }}>{office.working_hours}</div>}
          {office.google_maps_link && (
            <a href={office.google_maps_link} target="_blank" rel="noreferrer" className="sf-btn-outline" style={directionsBtn}>GET DIRECTIONS</a>
          )}
        </div>
      )}
      {!showCard && office.google_maps_link && (
        <a href={office.google_maps_link} target="_blank" rel="noreferrer" title="Open in Maps" style={smallMapBtn}>
          <i className="ti ti-external-link" />
        </a>
      )}
    </div>
  );
}

function BranchCard({ office }) {
  return (
    <div className="sf-branch-card">
      <div style={{ width: 200, flexShrink: 0, borderRadius: 12, overflow: 'hidden' }}>
        <MapPanel office={office} height={160} showCard={false} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ fontSize: 16.5, fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center' }}>
          <FlagIcon countryCode={office.country_code} size={18} />
          <Link to={`${STORE_BASE}/entity/${office.code}`} style={{ color: 'inherit', textDecoration: 'none' }} onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>{office.name}</Link>
        </h3>
        {office.address && <IconLine icon="ti-map-pin">{office.address}</IconLine>}
        {office.phone && (
          <IconLine icon="ti-phone">
            <a href={`tel:${office.phone.replace(/\s+/g, '')}`} style={{ color: 'inherit', textDecoration: 'none' }}
              onClick={() => trackEvent('phone_click', { location: 'contact_page_branch' })}>{office.phone}</a>
          </IconLine>
        )}
        {office.website && (
          <IconLine icon="ti-world">
            <a href={websiteHref(office.website)} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{office.website}</a>
          </IconLine>
        )}
        {office.email && <IconLine icon="ti-mail">{office.email}</IconLine>}
        {office.working_hours && (
          <>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#191919', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="ti ti-clock" style={{ fontSize: 12, color: 'var(--sf-accent, #c9920e)' }} /> Working Hours:
            </div>
            <div style={{ fontSize: 12.5, color: '#555', whiteSpace: 'pre-line', lineHeight: 1.6, marginLeft: 18 }}>{office.working_hours}</div>
          </>
        )}
        {office.google_maps_link && (
          <a href={office.google_maps_link} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, color: 'var(--sf-accent, #c9920e)', fontWeight: 700, fontSize: 12.5, textDecoration: 'none' }}>
            View Map <i className="ti ti-arrow-right" />
          </a>
        )}
      </div>
    </div>
  );
}

function websiteHref(url) {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function IconLine({ icon, children, multiline }) {
  return (
    <div style={{ display: 'flex', alignItems: multiline ? 'flex-start' : 'center', gap: 8, marginBottom: 5 }}>
      <i className={`ti ${icon}`} style={{ fontSize: 13, color: 'var(--sf-accent, #c9920e)', flexShrink: 0, marginTop: multiline ? 2 : 0 }} />
      <span style={{ fontSize: 13, color: '#555', whiteSpace: multiline ? 'pre-line' : 'normal', lineHeight: 1.5 }}>{children}</span>
    </div>
  );
}

function InfoRow({ icon, label, value, multiline }) {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#f7f8fa', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <i className={`ti ${icon}`} style={{ fontSize: 16, color: 'var(--sf-primary,#191919)' }} />
      </div>
      <div>
        <div style={{ fontSize: 11.5, color: '#999', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: '#222', whiteSpace: multiline ? 'pre-line' : 'normal', lineHeight: 1.6 }}>{value}</div>
      </div>
    </div>
  );
}

const lbl = { fontSize: 12.5, fontWeight: 500, color: '#555', display: 'block', marginBottom: 5 };
const inp = { width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
const errBox = { background: '#fef2f2', color: '#b91c1c', fontSize: 13, padding: '8px 11px', borderRadius: 8, marginBottom: 12 };
const submitBtn = { width: '100%', padding: 12, border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 4 };
const legalLink = { color: 'var(--sf-primary,#191919)', fontWeight: 600 };
const viewMapLink = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#f7f8fa',
  borderRadius: 16, color: 'var(--sf-primary,#191919)', fontWeight: 600, fontSize: 13.5, textDecoration: 'none',
};
const openMapBtn = {
  position: 'absolute', top: 16, left: 16, background: '#fff', borderRadius: 8, padding: '9px 16px', fontSize: 12.5, fontWeight: 700,
  color: '#191919', textDecoration: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
};
const mapCard = {
  position: 'absolute', bottom: 16, left: 16, right: 16, maxWidth: 280, background: '#fff', borderRadius: 12,
  padding: '18px 20px', boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
};
const smallMapBtn = {
  position: 'absolute', top: 8, right: 8, width: 26, height: 26, borderRadius: '50%', background: '#fff',
  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#191919', fontSize: 12,
  textDecoration: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
};
const directionsBtn = {
  display: 'inline-block', border: '1.5px solid var(--sf-accent)', borderRadius: 6, padding: '8px 16px',
  fontSize: 11.5, fontWeight: 700, textDecoration: 'none', letterSpacing: 0.3,
};
function I({ label, value, onChange, type = 'text', required }) {
  return <div style={{ marginBottom: 12 }}><label style={lbl}>{label}</label><input type={type} value={value} onChange={onChange} style={inp} required={required} /></div>;
}
